import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Button,
  Space,
  Popconfirm,
  App,
  Row,
  Col,
  Input,
  Grid,
  Tag,
  Tooltip,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  MailOutlined,
  EyeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { fetchEmployees, removeEmployee } from "../employeeThunks";
import { userEmailSentApi } from "../../../apiservice/setting";
import { resetViewMode, setViewMode } from "../employeesSlice";
import Papa from "papaparse";
import { Modal, Spin, Select, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import { BACKEND_API_URL } from "../../../environment";
import { fetchUserRoles, fetchBranchList } from "../../../store/commonThunks/lookups";
import { useIsAdminUser } from "../../../lib/accessList";
import { getCurrencySymbol } from "../../../lib/currency";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const EmployeeList = ({ onEdit, onCreate, authInfo, access_type }) => {
  const dispatch = useDispatch();
  const { message } = App.useApp();
  const { list: employees = [], loading = false } = useSelector(
    (s) => s.employees || {},
  );
  
  const [search, setSearch] = useState("");
  const { userRoles = [], branches = [] } = useSelector((s) => s.lookups);

  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [previewDataModal, setPreviewDataModal] = useState(false);
  const [previewData, setPreviewData] = useState([]);

  const [downloadURL, setDownloadURL] = useState(null);

  const [selectedUserRole, setSelectedUserRole] = useState(null);
  const [skippedRows, setSkippedRows] = useState([]);
  const [skippedRowsModal, setSkippedRowsModal] = useState(false);
  const [loginMode, setLoginMode] = useState("auto");
  const [sendEmail, setSendEmail] = useState(true);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.sm; // true when screen < 576px
  const isAdminUser = useIsAdminUser();

  const symbol = getCurrencySymbol(authInfo?.currency);

  const isHRUser = authInfo?.userInfo?.user_role_names?.includes("_hr");
  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;

  useEffect(() => {
    dispatch(fetchEmployees());
    dispatch(fetchUserRoles());
    dispatch(fetchBranchList());
  }, [dispatch]);

  const handleDelete = async (id) => {
    try {
      await dispatch(removeEmployee(id)).unwrap();
      message.success("Employee deleted");
      dispatch(fetchEmployees());
    } catch (err) {
      message.error(err?.message || "Delete failed");
    }
  };

  const userPasswordUpdate = async (employee, index) => {
    try {
      message.loading({ content: "Sending email...", key: "mail" });

      const res = await userEmailSentApi({
        id: employee?.user_id,
        branch_id: employee.branch_id,
        email: employee.email_address,
        name: employee.employee_name,
        index,
      });

      if (res?.data?.error === false) {
        message.success({ content: res.data.message, key: "mail" });
      } else {
        message.warning({
          content: res?.data?.message || "Failed",
          key: "mail",
        });
      }
    } catch (err) {
      console.error(err);
      message.error({
        content:
          err.response?.data?.message ||
          "Internal Error. Please contact support.",
        key: "mail",
      });
    }
  };

  const filtered = useMemo(() => {
    if (!search) return employees;
    const q = search.toLowerCase();
    return employees.filter(
      (e) =>
        (e.full_name || "").toLowerCase().includes(q) ||
        (e.employee_code || "").toLowerCase().includes(q) ||
        (e.email || "").toLowerCase().includes(q),
    );
  }, [employees, search]);

  // =================== TABLE COLUMNS ===================
  const columns = useMemo(
    () => [
      {
        title: "No.",
        key: "index",
        align: "center",
        // width: 70,
        render: (_, __, index) => index + 1,
      },
      {
        title: "Code",
        dataIndex: "employee_code",
        key: "employee_code",
        sorter: (a, b) =>
          (a.employee_code || "").localeCompare(b.employee_code || ""),
        // width: 100,
      },
      {
        title: "Company ID",
        dataIndex: "company_employee_id",
        key: "company_employee_id",
        sorter: (a, b) =>
          (a.company_employee_id || "").localeCompare(
            b.company_employee_id || "",
          ),
        // width: 140,
        responsive: ["sm"],
      },
      {
        title: "Employee Name",
        dataIndex: "employee_name",
        key: "employee_name",
        sorter: (a, b) =>
          (a.employee_name || "").localeCompare(b.employee_name || ""),
        // width: 150,
        ellipsis: true,
      },
      {
        title: "Total Salary",
        dataIndex: "total_monthly_salary",
        key: "total_monthly_salary",
        align: "right",
        render: (val) =>
          val ? `${symbol} ${Number(val).toLocaleString("en-IN")}` : "-",
        sorter: (a, b) =>
          Number(a.total_monthly_salary || 0) -
          Number(b.total_monthly_salary || 0),
        // width: 120,
      },
      {
        title: "Contact No",
        dataIndex: "contact_no",
        key: "contact_no",
        responsive: ["md"],
        // width: 140,
      },
      {
        title: "Joining Date",
        dataIndex: "joining_date",
        key: "joining_date",
        render: (val) => (val ? moment(val).format("DD-MM-YYYY") : "N/A"),
        sorter: (a, b) => new Date(a.joining_date) - new Date(b.joining_date),
        // width: 140,
      },
      {
        title: "Target Amount",
        dataIndex: "target_amount",
        key: "target_amount",
        align: "right",
        responsive: ["md"],
        // width: 140,
        render: (val) =>
          val ? `${symbol} ${Number(val).toLocaleString("en-IN")}` : "-",
      },
      {
        title: "Commission %",
        dataIndex: "commission_per",
        key: "commission_per",
        align: "center",
        responsive: ["md"],
        // width: 140,
        render: (val) => (val ? `${val}%` : "-"),
      },
      {
        title: "Status",
        dataIndex: "employee_status",
        key: "employee_status",
        align: "center",
        render: (status) => {
          if (!status) return "-";
          const color =
            status.toLowerCase() === "active"
              ? "green"
              : status.toLowerCase() === "terminated"
                ? "red"
                : "blue";
          return <Tag color={color}>{status}</Tag>;
        },
      },
      {
        title: "Actions",
        key: "actions",
        align: "right",
        fixed: "right",
        render: (_, record, index) => (
          <Space size={isMobile ? 0 : "small"}>
            {access_type === "WRITE" && (
              <Tooltip title="Resend mail to reset Password">
                <Button
                  type="text"
                  size={isMobile ? "small" : "middle"}
                  icon={<MailOutlined style={{ color: "#1677FF" }} />}
                  onClick={() => userPasswordUpdate(record, index)}
                />
              </Tooltip>
            )}
            <Tooltip title="View Employee (Read Only)">
              <Button
                type="text"
                size={isMobile ? "small" : "middle"}
                icon={<EyeOutlined />}
                onClick={() => {
                  onEdit(record, "view-mode");
                  dispatch(setViewMode());
                }}
              />
            </Tooltip>
            {access_type === "WRITE" && (
              <Tooltip title="Edit Employee">
                <Button
                  type="text"
                  size={isMobile ? "small" : "middle"}
                  icon={<EditOutlined />}
                  onClick={() => {
                    onEdit(record, "edit-mode");
                    dispatch(resetViewMode());
                  }}
                  disabled={!(isAdminUser || isHRUser || canEdit)}
                />
              </Tooltip>
            )}
            {access_type === "WRITE" && (
              <Popconfirm
                title="Delete employee?"
                onConfirm={() => handleDelete(record.employee_id)}
              >
                <Button
                  type="text"
                  size={isMobile ? "small" : "middle"}
                  disabled={!(isAdminUser || isHRUser || canDelete)}
                  danger
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ],
    [onEdit, isMobile],
  );

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(
        `${BACKEND_API_URL}/hrpayroll/employee/template`,
        {
          method: "GET",
          headers: {
            "auth-token": authInfo.token,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) throw new Error("Failed to download template");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "employee_Upload_Template.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      message.error("Failed to download template");
    }
  };


  const headerMap = {
    employee_name: "Employee Name",
    email_address: "Email",
    department_name: "Department",
    designation_name: "Designation",
    joining_date: "Joining Date",
    branch: "Branch",
    contact_no: "Contact",
  };

  const handleFileUpload = (info) => {
    const fileObj = info.file.originFileObj;
    setFile(fileObj);   
    if (!fileObj) return;

    const MAX_FILE_SIZE = 524288; // 50 KB
    if (fileObj.size > MAX_FILE_SIZE) {
      Modal.warning({
        title: "File too large",
        content: "File size exceeds 50 KB. Please upload a smaller CSV.",
      });
      return;
    }

    if (fileObj.type !== "text/csv" && !fileObj.name.endsWith(".csv")) {
      Modal.error({
        title: "Invalid File Type",
        content: "Please upload a valid CSV file.",
      });
      return;
    }

    setIsLoading(true);

    Papa.parse(fileObj, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {       

        const normalizedData = results.data.map(row => {
          const newRow = {};
          Object.keys(row).forEach(key => {
            const normalizedKey = key.trim().toLowerCase().replace(/\s+/g, "_");

            let value = row[key];

            // 🔥 CLEAN VALUE
            if (typeof value === "string") {
              value = value.trim();
            }
            if (key === "joining_date" && value) {
              const parsed = dayjs(value, ["DD/MM/YYYY", "YYYY-MM-DD", "MM/DD/YYYY"], true);

              if (parsed.isValid()) {
                value = parsed.format("YYYY-MM-DD");
              } else {                
                value = ""; // or handle error
              }
            }

            newRow[normalizedKey] = value;
            newRow._action = row.email_address ? "auto" : "no-login";
          });
          return newRow;
        });       
        const requiredColumns = [
          "company_employee_id",
          "employee_name",
          "gender_type",
          "marital_type",
          "birth_date",
          "email_address",
          "zip_code",
          "present_address",
          "permanent_address",
          "country_code",
          "contact_no",
          "emergency_name",
          "emergency_relation",
          "emergency_country_code",
          "emergency_contact_no",
          "national_id_no",
          "national_id_expiry",
          "passport_no",
          "visa_no",
          "passport_expiry",
          "visa_expiry",
          "labour_contract_id",
          "labour_contract_expiry",
          "client_labour_contract_expiry",
          "insurance_name",
          "insurance_status",
          "insurance_expiry",
          "father_name",
          "mother_name",
          "department_name",
          "designation_name",
          "joining_date",
          "reporting_manager",
          //"map_user",
          "branch",
        ];

        const fields = (results.meta?.fields || []).map(f =>
          f.trim().toLowerCase().replace(/\s+/g, "_")
        );

        if (!requiredColumns.every((c) => fields.includes(c))) {
          Modal.error({
            title: "Invalid File Structure",
            content: "Uploaded CSV does not match the required template.",
          });
          setIsLoading(false);
          return;
        }

        // Validate rows
        let isValid = true;
        const nameSet = new Set();
        const emailSet = new Set();

        for (let index = 0; index < normalizedData.length; index++) {
          const row = normalizedData[index];
          const rowNum = index + 2;
         
          if (!row.employee_name) {
            message.error(`Row ${rowNum}: Employee Name required`);
            isValid = false;
            break;
          }

          if (!row.email_address) {
            message.error(`Row ${rowNum}: Email required`);
            isValid = false;
            break;
          }

          if (!row.joining_date) {
            message.error(`Row ${rowNum}: Joining Date required`);
            isValid = false;
            break;
          }


          if (!row.department_name) {
            message.error(`Row ${rowNum}: Department Name required`);
            isValid = false;
            break;
          }
          if (!row.designation_name) {
            message.error(`Row ${rowNum}: Designation Name required`);
            isValid = false;
            break;
          }
          if (!row.gender_type) {
            message.error(`Row ${rowNum}: Gender required`);
            isValid = false;
            break;
          }


          // Duplicate name check
          const nameKey = row.employee_name.trim().toLowerCase();
          if (nameSet.has(nameKey)) {
            message.error(`Row ${rowNum}: Duplicate Employee Name`);
            isValid = false;
            break;
          }
          nameSet.add(nameKey);

          // Duplicate email check
          const emailKey = row.email_address.trim().toLowerCase();
          if (emailSet.has(emailKey)) {
            message.error(`Row ${rowNum}: Duplicate Email`);
            isValid = false;
            break;
          }
          emailSet.add(emailKey);
        }

        if (isValid) {
          setPreviewData(normalizedData);
          setPreviewDataModal(true);
        }

        setIsLoading(false);
      },
    });
  };

  const submitFileUpload = async () => {
    if (!selectedBranch) {
      message.warning("Please select a branch");
      return;
    }

    if (!selectedUserRole?.value) {
      message.warning("Please choose a user role");
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("branch_id", selectedBranch);
    formData.append("selectedUserRoleLabel", selectedUserRole.label);
    formData.append("selectedUserRoleValue", selectedUserRole.value);
    formData.append("sendEmail", sendEmail);
    formData.append("loginMode", loginMode);

    try {
      // const res = await postData("/hrpayroll/employee/upload-csv", formData);

      const res = await axios.post(
        `${BACKEND_API_URL}/hrpayroll/employee/upload-csv`,
        formData,
        {
          headers: {
            "auth-token": authInfo.token,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      // HANDLE ERROR FIRST
      if (res.data?.error) {
        Modal.error({
          title: "Upload Failed",
          content: res.data.msg,
        });
        return;
      }

      // SUCCESS FLOW
      if (res.data?.downloadUrl) {
        setDownloadURL(`${BACKEND_API_URL}${res.data.downloadUrl}`);
      }

      if (res.data?.skippedRows?.length > 0) {
        setSkippedRows(res.data.skippedRows);
        setSkippedRowsModal(true);
        message.warning(`${res.data.skippedRows.length} rows skipped`);
      } else {
        message.success("CSV imported successfully");
      }

      setPreviewDataModal(false);
      dispatch(fetchEmployees());
    } catch (err) {
      console.error(err);
      message.error("Upload failed");
    }

    setIsLoading(false);
  };

  return (
    <div>
      <Row
        gutter={[12, 12]}
        align="middle"
        style={{ marginBottom: 16 }}
      >
        {/* LEFT: Title */}
        <Col xs={24} md={8}>
          <h3 style={{ margin: 0 }}>Employees</h3>
        </Col>

        {/* RIGHT: Actions */}
        <Col xs={24} md={16}>
          <div
            style={{
              display: "flex",
              justifyContent: isMobile ? "flex-start" : "flex-end",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            {/* Upload + Download Group */}
            {access_type === "WRITE" && (
              <Space wrap>
                <Upload
                  accept=".csv"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    handleFileUpload({ file: { originFileObj: file } });
                    return false;
                  }}
                >
                  <Button icon={<UploadOutlined />}>Upload CSV</Button>
                </Upload>

                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadTemplate}
                >
                  Template
                </Button>
              </Space>
            )}

            {/* Search */}
            <Input.Search
              placeholder="Search employee..."
              allowClear
              onSearch={(val) => setSearch(val)}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: isMobile ? "100%" : 220,
              }}
            />

            {/* Primary CTA */}
            {access_type === "WRITE" && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={onCreate}
              >
                Add Employee
              </Button>
            )}
          </div>
        </Col>
      </Row>

      <Table
        rowKey={(r) => r.id || r.employee_id}
        dataSource={filtered}
        columns={columns}
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: filtered.length,
          showQuickJumper: true,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: (page) => {
            setCurrentPage(page);
          },
          onShowSizeChange: (_, size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
        }}
        scroll={{ x: "max-content" }}
        tableLayout="auto"
        size="middle"
      />

      <Modal
        open={previewDataModal}
        onCancel={() => setPreviewDataModal(false)}
        footer={null}
        width="80%"
      >
        {isLoading ? (
          <Spin />
        ) : (

          !previewData.length ? <div>No data</div> :
            <>
              <h2>CSV File Preview</h2>

              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>

                <div>Rows: {previewData.length}</div>

                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>

                  <Select
                    placeholder="Select Branch"
                    options={branches.map(b => ({ label: b.name, value: b.id }))}
                    style={{ width: 200 }}
                    onChange={(value) => setSelectedBranch(value)}
                  />

                  <Select
                    placeholder="User Role"
                    options={userRoles}
                    style={{ width: 200 }}
                    onChange={(value, option) =>
                      setSelectedUserRole({ value, label: option.display })
                    }
                  />

                  <Select
                    value={loginMode}
                    style={{ width: 180 }}
                    onChange={(val) => setLoginMode(val)}
                    options={[
                      { label: "Auto (Recommended)", value: "auto" },
                      { label: "Create Login", value: "create" },
                      { label: "Map Only", value: "map" },
                    ]}
                  />

                  <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                    />
                    Send Email
                  </label>

                  <Button type="primary" onClick={submitFileUpload}>
                    Upload
                  </Button>

                </div>
              </div>

              <div style={{ overflowX: "auto", marginTop: 20 }}>
                {/*<table className="table table-bordered" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    {Object.keys(previewData[0] || {}).map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, i) => (
                    <tr key={i}>
                      {Object.values(row).map((v, j) => (
                        <td key={j}>{v}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>*/}
                <Table
                  dataSource={previewData}
                  rowKey={(r, i) => i}
                  pagination={false}
                  scroll={{ x: true }}
                  columns={[
                    {
                      title: "Employee",
                      dataIndex: "employee_name",
                    },
                    {
                      title: "Email",
                      dataIndex: "email_address",
                    },
                    {
                      title: "Department",
                      dataIndex: "department_name",
                    },
                    {
                      title: "Designation",
                      dataIndex: "designation_name",
                    },
                    {
                      title: "Joining Date",
                      dataIndex: "joining_date",
                    },
                    {
                      title: "Branch",
                      render: () => {
                        const branch = branches.find(b => b.id === selectedBranch);
                        return branch ? branch.name : "-";
                      }
                    },
                    {
                      title: "Login Action",
                      render: (_, row) => {
                        if (!row.email_address)
                          return <Tag color="default">No Login</Tag>;

                        return loginMode === "map"
                          ? <Tag color="blue">Map Only</Tag>
                          : loginMode === "create"
                            ? <Tag color="green">Create Login</Tag>
                            : <Tag color="purple">Auto</Tag>;
                      },
                    },
                    {
                      title: "Status",
                      render: (_, row) => {
                        if (!row.employee_name || !row.email_address)
                          return <Tag color="red">Invalid</Tag>;

                        return <Tag color="green">Ready</Tag>;
                      },
                    }

                  ]}
                  expandable={{
                    expandedRowRender: (row) => (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                        {Object.entries(row).map(([key, value]) =>
                          value ? (
                            <div key={key}>
                              <strong>{key}:</strong> {value}
                            </div>
                          ) : null
                        )}
                      </div>
                    ),
                  }}
                />
              </div>
            </>
        )}
      </Modal>

      <Modal
        open={skippedRowsModal}
        onCancel={() => setSkippedRowsModal(false)}
        footer={null}
        width={"70%"}
      >
        <h2>Skipped Rows Preview</h2>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            paddingBottom: "10px",
          }}
        >
          <span>Total Skipped: {skippedRows.length}</span>

          <Button
            type="primary"
            onClick={async () => {
              try {
                const response = await axios.get(downloadURL, {
                  headers: { "auth-token": authInfo.token },
                  responseType: "blob",
                });

                const blob = new Blob([response.data], { type: "text/csv" });
                const link = document.createElement("a");
                link.href = window.URL.createObjectURL(blob);
                link.setAttribute("download", "skipped_rows.csv");
                document.body.appendChild(link);
                link.click();
                link.remove();

                setSkippedRowsModal(false);
              } catch {
                message.error("Failed to download skipped rows.");
              }
            }}
          >
            Download Skipped Rows
          </Button>
        </div>

        <div
          style={{
            maxHeight: "70vh",
            overflowY: "auto",
            overflowX: "auto",
            border: "1px solid #ddd",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {Object.keys(skippedRows[0] || {}).map((key) => (
                  <th
                    key={key}
                    style={{
                      padding: 10,
                      background: "#f0f0f0",
                      border: "1px solid #ddd",
                    }}
                  >
                    {key}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {skippedRows.map((row, index) => (
                <tr key={index}>
                  {Object.values(row).map((value, idx) => (
                    <td
                      key={idx}
                      style={{
                        padding: 10,
                        border: "1px solid #ddd",
                      }}
                    >
                      {value}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>
    </div>
  );
};

export default EmployeeList;
